/**
 * Canonical list of ISO 639-1 two-letter language codes. Used both to validate
 * incoming codes/locale tags and to derive the name → code lookup below, so the
 * function only ever emits a canonical code (never a deprecated alias).
 */
const ISO_639_1_CODES = [
  "aa",
  "ab",
  "ae",
  "af",
  "ak",
  "am",
  "an",
  "ar",
  "as",
  "av",
  "ay",
  "az",
  "ba",
  "be",
  "bg",
  "bh",
  "bi",
  "bm",
  "bn",
  "bo",
  "br",
  "bs",
  "ca",
  "ce",
  "ch",
  "co",
  "cr",
  "cs",
  "cu",
  "cv",
  "cy",
  "da",
  "de",
  "dv",
  "dz",
  "ee",
  "el",
  "en",
  "eo",
  "es",
  "et",
  "eu",
  "fa",
  "ff",
  "fi",
  "fj",
  "fo",
  "fr",
  "fy",
  "ga",
  "gd",
  "gl",
  "gn",
  "gu",
  "gv",
  "ha",
  "he",
  "hi",
  "ho",
  "hr",
  "ht",
  "hu",
  "hy",
  "hz",
  "ia",
  "id",
  "ie",
  "ig",
  "ii",
  "ik",
  "io",
  "is",
  "it",
  "iu",
  "ja",
  "jv",
  "ka",
  "kg",
  "ki",
  "kj",
  "kk",
  "kl",
  "km",
  "kn",
  "ko",
  "kr",
  "ks",
  "ku",
  "kv",
  "kw",
  "ky",
  "la",
  "lb",
  "lg",
  "li",
  "ln",
  "lo",
  "lt",
  "lu",
  "lv",
  "mg",
  "mh",
  "mi",
  "mk",
  "ml",
  "mn",
  "mr",
  "ms",
  "mt",
  "my",
  "na",
  "nb",
  "nd",
  "ne",
  "ng",
  "nl",
  "nn",
  "no",
  "nr",
  "nv",
  "ny",
  "oc",
  "oj",
  "om",
  "or",
  "os",
  "pa",
  "pi",
  "pl",
  "ps",
  "pt",
  "qu",
  "rm",
  "rn",
  "ro",
  "ru",
  "rw",
  "sa",
  "sc",
  "sd",
  "se",
  "sg",
  "sh",
  "si",
  "sk",
  "sl",
  "sm",
  "sn",
  "so",
  "sq",
  "sr",
  "ss",
  "st",
  "su",
  "sv",
  "sw",
  "ta",
  "te",
  "tg",
  "th",
  "ti",
  "tk",
  "tl",
  "tn",
  "to",
  "tr",
  "ts",
  "tt",
  "tw",
  "ty",
  "ug",
  "uk",
  "ur",
  "uz",
  "ve",
  "vi",
  "vo",
  "wa",
  "wo",
  "xh",
  "yi",
  "yo",
  "za",
  "zh",
  "zu",
] as const;

const VALID_CODES = new Set<string>(ISO_639_1_CODES);

let nameToCode: Map<string, string> | null = null;

/**
 * Lazily builds (and memoizes) a lowercased language-name → ISO 639-1 code map.
 * Names are derived from the runtime's Intl data for both English and the
 * language's own native name, so "English"/"español"/"中文" all resolve.
 */
const getNameToCode = (): Map<string, string> => {
  if (nameToCode) return nameToCode;

  const map = new Map<string, string>();
  const englishNames = new Intl.DisplayNames(["en"], { type: "language", fallback: "none" });

  for (const code of ISO_639_1_CODES) {
    const english = englishNames.of(code);
    if (english) map.set(english.toLowerCase(), code);

    const native = new Intl.DisplayNames([code], { type: "language", fallback: "none" }).of(code);
    if (native) map.set(native.toLowerCase(), code);
  }

  nameToCode = map;
  return map;
};

/**
 * Converts an arbitrary language string into its ISO 639-1 two-letter code.
 *
 * Accepts, case-insensitively:
 * - Bare ISO 639-1 codes — `"es"` → `"es"`
 * - BCP-47 / locale tags — `"es-ES"`, `"en_US"`, `"zh-Hans-CN"` → primary subtag (`"es"`, `"en"`, `"zh"`)
 * - English language names — `"English"` → `"en"`, `"Spanish"` → `"es"`
 * - Native language names — `"Español"` → `"es"`, `"中文"` → `"zh"`
 *
 * Returns `undefined` when the input is empty or cannot be resolved to a code.
 * Three-letter ISO 639-2/3 codes (e.g. `"eng"`) are not supported.
 *
 * @example
 * getISO6391Code("English");  // "en"
 * getISO6391Code("es-ES");    // "es"
 * getISO6391Code("fr");       // "fr"
 * getISO6391Code("klingon");  // undefined
 */
export const getISO6391Code = (input: string | null | undefined): string | undefined => {
  if (!input) return undefined;

  const trimmed = input.trim();
  if (!trimmed) return undefined;

  // Locale tag or bare code: take the primary language subtag (e.g. "es-ES" -> "es").
  const [primarySubtag = ""] = trimmed.split(/[-_]/);
  const normalizedSubtag = primarySubtag.toLowerCase();
  if (VALID_CODES.has(normalizedSubtag)) return normalizedSubtag;

  // Fall back to an English or native language name (e.g. "Spanish"/"Español" -> "es").
  return getNameToCode().get(trimmed.toLowerCase());
};
